import * as React from "react";
import { useLocation } from "wouter";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { authService } from "@/services/authService";

function useQueryParam(name: string): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const value = params.get(name);
  return value;
}

export function ResetPasswordPage() {
  const [, setLocation] = useLocation();
  const token = useQueryParam("token");

  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  const isTokenMissing = !token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    setErrorMessage(null);
    setSuccessMessage(null);

    if (!password || password.length < 6) {
      setErrorMessage("Password minimal 6 karakter.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Konfirmasi password tidak sama.");
      return;
    }

    setIsSubmitting(true);

    try {
      await authService.resetPassword(token, password);
      setSuccessMessage("Password berhasil diubah. Silakan login kembali.");
    } catch (error) {
      console.error("Failed to reset password:", error);
      setErrorMessage("Link reset tidak valid atau sudah kedaluwarsa.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToLogin = () => {
    setLocation("/login");
  };

  return (
    <Card className="w-full shadow-lg border-border/50">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
        <CardDescription className="text-muted-foreground">
          {isTokenMissing
            ? "Link reset password tidak valid."
            : "Masukkan password baru Anda."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isTokenMissing ? (
          <div className="space-y-4">
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
              Link reset tidak valid atau token tidak ditemukan.
            </div>
            <Button type="button" className="w-full" onClick={handleBackToLogin}>
              Kembali ke login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password">Password Baru</Label>
              <Input
                id="password"
                type="password"
                placeholder="Password baru"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Konfirmasi Password Baru</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Ulangi password baru"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>

            {errorMessage && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg border border-destructive/20">
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-50 text-green-800 text-sm p-3 rounded-lg border border-green-200 dark:bg-green-950/50 dark:text-green-200 dark:border-green-800">
                {successMessage}
              </div>
            )}

            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={isSubmitting || !!successMessage}>
                {isSubmitting ? "Menyimpan..." : "Simpan Password"}
              </Button>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1.5 underline underline-offset-4 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Kembali ke login
              </button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
